import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ShieldCheck } from "lucide-react";

const LOGO = "/logodefence.png";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/ecosystem", label: "Ecosystem" },
  { to: "/enterprise-security", label: "Enterprise Security" },
  { to: "/awareness", label: "Awareness" },
  { to: "/workforce", label: "Workforce" },
  { to: "/alliances", label: "Alliances" },
  { to: "/labs", label: "Labs" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      {/* Operational status strip — futuristic enterprise feel */}
      <div data-testid="status-strip" className="hidden md:block fixed top-0 inset-x-0 z-[60] h-7 bg-[#0A1B5E] text-white">
        <div className="container-cd h-full flex items-center justify-between text-[10.5px] font-mono-tech tracking-[0.22em] uppercase">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> SOC · Operational
            </span>
            <span className="hidden lg:flex items-center gap-2 text-slate-300">
              <ShieldCheck size={11} className="text-cyan-300" /> DPDP · ISO 27001 Aligned
            </span>
          </div>
          <div className="flex items-center gap-5 text-slate-300">
            <span className="hidden lg:inline">1930 · National Cyber Helpline</span>
            <a href="tel:+919315649601" className="hover:text-cyan-300">+91 93156 49601</a>
          </div>
        </div>
      </div>

      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 z-50 transition-all duration-300 md:top-7 top-0 ${
          scrolled
            ? "bg-white/85 backdrop-blur-2xl border-b border-slate-200/70 shadow-[0_10px_40px_-18px_rgba(10,27,94,0.18)]"
            : "bg-white/65 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="container-cd flex items-center justify-between h-[85px]">
                  <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3 group">
                      <img
                          src={LOGO}
                          alt="Cosmos Defence"
                          className="h-[78px] w-auto object-contain"
                      />
                  </Link>
          <nav className="hidden xl:flex items-center gap-5 2xl:gap-7" data-testid="navbar-menu">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3 ml-3">
            <Link to="/contact?type=consultation" data-testid="navbar-consult-btn" className="btn-accent text-[12.5px] !px-5 !py-2.5 whitespace-nowrap">
              Request Consultation <ArrowUpRight size={14} />
            </Link>
          </div>

          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden p-2 rounded-lg border border-slate-200 text-[#0A1B5E]"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {open && (
          <div className="xl:hidden bg-white/97 backdrop-blur-xl border-t border-slate-200 max-h-[80vh] overflow-y-auto" data-testid="navbar-mobile-menu">
            <div className="container-cd py-4 flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 px-3 rounded-lg font-display font-medium ${
                      isActive ? "bg-blue-50 text-[#0A1B5E]" : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link to="/contact?type=consultation" data-testid="mobile-navbar-consult-btn" className="btn-accent mt-2 justify-center">
                Request Consultation
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
